import { useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Link as RouterLink } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import {
  Calendar,
  FileText,
  AlertTriangle,
  MoreHorizontal,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { getStatusIcon } from '@/utils/getStatusIcon';
import { formatDate } from '@/utils/formatDate';
import {
  getPriorityColor,
  getStatusColor,
  isAssignmentOverdue,
} from '@/utils/assignments';
import { AssignmentProgress } from '../assignment/AssignmentProgress';
import { AssignmentTags } from '../assignment/AssignmentTags';
import { AssignmentCardDescription } from '../assignment/AssignmentCardDescription';
import { ContentViewer } from './ContentViewer';
import { deleteAssignmentActionAtom } from '@/stores/assignmentsStore';
import type { AssignmentResponse } from '@/types';
import { PreInterviewWorkflow } from './workflow/PreInterviewWorkflow';
import { PostInterviewWorkflow } from './workflow/PostInterviewWorkflow';
import { SourceName } from './SourceName';

export interface AssignmentCardProps {
  assignment: AssignmentResponse;
  isExpanded: boolean;
  onToggleExpanded: (assignmentId: string) => void;
}

export function AssignmentCard({
  assignment,
  isExpanded,
  onToggleExpanded,
}: AssignmentCardProps) {
  const navigate = useNavigate();
  const deleteAssignmentAction = useSetAtom(deleteAssignmentActionAtom);

  const handleDelete = async (assignmentId: string) => {
    if (!confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await deleteAssignmentAction(assignmentId);
      toast.success('Assignment deleted successfully');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete assignment';
      toast.error(errorMessage);
    }
  };
  return (
    <Card className='hover:bg-accent/20 transition-colors'>
      <CardHeader className='pb-3'>
        <div className='flex items-start justify-between'>
          <div className='flex-1'>
            <div className='flex items-center gap-2 mb-2'>
              {getStatusIcon(assignment.status)}
              <Badge className={getStatusColor(assignment.status)}>
                {assignment.status.replace('_', ' ')}
              </Badge>
              <Badge variant={getPriorityColor(assignment.priority) as any}>
                {assignment.priority}
              </Badge>
              {isAssignmentOverdue(assignment) && (
                <Badge
                  variant='destructive'
                  className='flex items-center gap-1'
                >
                  <AlertTriangle className='h-3 w-3' />
                  Overdue
                </Badge>
              )}
              <span className='text-sm text-muted-foreground'>
                Created {formatDate(assignment.createdAt)}
              </span>
            </div>
            <RouterLink to={`/assignments/${assignment.id}`}>
              <CardTitle className='text-lg text-left mb-1'>
                {assignment.title}
              </CardTitle>
            </RouterLink>
            {assignment.description && (
              <AssignmentCardDescription description={assignment.description} />
            )}

            <div className='flex items-center gap-4 text-sm text-muted-foreground mb-2'>
              {assignment.tipId && (
                <div className='flex items-center gap-1'>
                  <FileText className='h-3 w-3' />
                  <span>Tip: {assignment.tipId.slice(0, 8)}...</span>
                </div>
              )}
              {assignment.sourceId && (
                <SourceName sourceId={assignment.sourceId} />
              )}
            </div>

            {assignment.deadline && (
              <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                <Calendar className='h-3 w-3' />
                <span>Due: {formatDate(assignment.deadline)}</span>
              </div>
            )}
          </div>
          <div className='flex items-start gap-2'>
            <Button
              variant='ghost'
              size='sm'
              onClick={() => onToggleExpanded(assignment.id)}
              className='shrink-0'
            >
              {isExpanded ? (
                <ChevronUp className='h-4 w-4' />
              ) : (
                <ChevronDown className='h-4 w-4' />
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant='ghost' size='sm' className='shrink-0'>
                  <MoreHorizontal className='h-4 w-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem asChild>
                  <RouterLink to={`/assignments/${assignment.id}/edit`}>
                    <Edit className='mr-2 h-4 w-4' />
                    Edit Assignment
                  </RouterLink>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => handleDelete(assignment.id)}
                  className='text-destructive'
                >
                  <Trash2 className='mr-2 h-4 w-4' />
                  Delete Assignment
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        <AssignmentProgress assignment={assignment} />

        <div className='space-y-4'>
          <PreInterviewWorkflow assignment={assignment} />
          <PostInterviewWorkflow assignment={assignment} />
        </div>

        <Collapsible
          open={isExpanded}
          onOpenChange={() => onToggleExpanded(assignment.id)}
        >
          <CollapsibleContent>
            <Separator className='my-4' />
            <ContentViewer assignment={assignment} />
          </CollapsibleContent>
        </Collapsible>

        <div className='justify-self-start'>
          <AssignmentTags assignment={assignment} />
        </div>

        <div className='flex gap-2'>
          <Button
            variant='default'
            size='sm'
            onClick={() => navigate(`/assignments/${assignment.id}`)}
            className='gap-2'
          >
            View details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
